import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Building2, Users, Calendar, FolderOpen, Loader2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { cn } from '@/lib/utils';

export function HeaderSearchDropdown() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debouncedSearch = useDebouncedValue(search, 250);

  // Keyboard shortcut for global search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
      if (e.key === 'Escape' && open) {
        setOpen(false);
        setSearch('');
        inputRef.current?.blur();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  // Open popover when search has content
  useEffect(() => {
    setOpen(debouncedSearch.length >= 2);
  }, [debouncedSearch]);

  // Query leads
  const { data: leads, isFetching: leadsFetching } = useQuery({
    queryKey: ['header-search-leads', debouncedSearch],
    queryFn: async () => {
      if (debouncedSearch.length < 2) return [];
      const { data } = await supabase
        .from('leads')
        .select('id, company, contact_name, status')
        .or(`company.ilike.%${debouncedSearch}%,contact_name.ilike.%${debouncedSearch}%`)
        .limit(5);
      return data || [];
    },
    enabled: debouncedSearch.length >= 2,
  });

  // Query clients
  const { data: clients, isFetching: clientsFetching } = useQuery({
    queryKey: ['header-search-clients', debouncedSearch],
    queryFn: async () => {
      if (debouncedSearch.length < 2) return [];
      const { data } = await supabase
        .from('clients')
        .select('id, company_name')
        .ilike('company_name', `%${debouncedSearch}%`)
        .limit(5);
      return data || [];
    },
    enabled: debouncedSearch.length >= 2,
  });

  // Query appointments
  const { data: appointments, isFetching: appointmentsFetching } = useQuery({
    queryKey: ['header-search-appointments', debouncedSearch],
    queryFn: async () => {
      if (debouncedSearch.length < 2) return [];
      const { data } = await supabase
        .from('appointments')
        .select('id, title, start_at')
        .ilike('title', `%${debouncedSearch}%`)
        .limit(5);
      return data || [];
    },
    enabled: debouncedSearch.length >= 2,
  });

  // Query dossiers
  const { data: dossiers, isFetching: dossiersFetching } = useQuery({
    queryKey: ['header-search-dossiers', debouncedSearch],
    queryFn: async () => {
      if (debouncedSearch.length < 2) return [];
      const { data } = await supabase
        .from('dossiers')
        .select('id, status, lead:leads(company), product:products(name)')
        .limit(5);
      // Filter in JS since we need to search on joined table
      return (data || []).filter(d => 
        d.lead?.company?.toLowerCase().includes(debouncedSearch.toLowerCase())
      );
    },
    enabled: debouncedSearch.length >= 2,
  });

  const isFetching = leadsFetching || clientsFetching || appointmentsFetching || dossiersFetching;
  const hasResults = (leads?.length || 0) + (clients?.length || 0) + (appointments?.length || 0) + (dossiers?.length || 0) > 0;

  const handleSelect = (path: string) => {
    navigate(path);
    setOpen(false);
    setSearch('');
    inputRef.current?.blur();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className="relative w-full max-w-md" data-tour="global-search">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            ref={inputRef}
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher... (⌘K)"
            className="pl-9 bg-secondary/50 border-0 focus-visible:ring-1"
          />
          {isFetching && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground animate-spin" />
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent 
        className="w-[400px] p-0" 
        align="start" 
        sideOffset={4}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <ScrollArea className="max-h-[400px]">
          {debouncedSearch.length < 2 ? (
            <div className="p-4 text-sm text-muted-foreground text-center">
              Tapez au moins 2 caractères...
            </div>
          ) : !hasResults && !isFetching ? (
            <div className="p-4 text-sm text-muted-foreground text-center">
              Aucun résultat pour "{debouncedSearch}"
            </div>
          ) : (
            <div className="py-2">
              {/* Leads */}
              {leads && leads.length > 0 && (
                <div>
                  <div className="px-3 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Leads
                  </div>
                  {leads.map((lead) => (
                    <button
                      key={lead.id}
                      onClick={() => handleSelect(`/leads/${lead.id}`)}
                      className={cn(
                        "w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-accent cursor-pointer text-left",
                        "focus:bg-accent focus:outline-none"
                      )}
                    >
                      <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{lead.company}</div>
                        {lead.contact_name && (
                          <div className="text-xs text-muted-foreground truncate">{lead.contact_name}</div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Clients */}
              {clients && clients.length > 0 && (
                <div>
                  <div className="px-3 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Clients
                  </div>
                  {clients.map((client) => (
                    <button
                      key={client.id}
                      onClick={() => handleSelect(`/clients/${client.id}`)}
                      className={cn(
                        "w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-accent cursor-pointer text-left",
                        "focus:bg-accent focus:outline-none"
                      )}
                    >
                      <Users className="h-4 w-4 text-muted-foreground shrink-0" />
                      <div className="font-medium truncate">{client.company_name}</div>
                    </button>
                  ))}
                </div>
              )}

              {/* Appointments */}
              {appointments && appointments.length > 0 && (
                <div>
                  <div className="px-3 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Rendez-vous
                  </div>
                  {appointments.map((apt) => (
                    <button
                      key={apt.id}
                      onClick={() => handleSelect('/planning')}
                      className={cn(
                        "w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-accent cursor-pointer text-left",
                        "focus:bg-accent focus:outline-none"
                      )}
                    >
                      <Calendar className="h-4 w-4 text-muted-foreground shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{apt.title}</div>
                        <div className="text-xs text-muted-foreground">
                          {new Date(apt.start_at).toLocaleDateString('fr-FR')}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Dossiers */}
              {dossiers && dossiers.length > 0 && (
                <div>
                  <div className="px-3 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Dossiers
                  </div>
                  {dossiers.map((dossier) => (
                    <button
                      key={dossier.id}
                      onClick={() => handleSelect(`/dossiers/${dossier.id}`)}
                      className={cn(
                        "w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-accent cursor-pointer text-left",
                        "focus:bg-accent focus:outline-none"
                      )}
                    >
                      <FolderOpen className="h-4 w-4 text-muted-foreground shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{dossier.lead?.company || 'Dossier'}</div>
                        {dossier.product?.name && (
                          <div className="text-xs text-muted-foreground truncate">{dossier.product.name}</div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
